/**
 * Editor for one inspector on one day.
 *
 * Opens from a cell in the availability grid. Inspectors reach their own row;
 * admins and schedulers reach anyone's. The permission is enforced again on
 * the backend — this only decides what to render.
 *
 * Two kinds of thing show up here and they are not equal. Blocks declared in
 * the dashboard are ours and can be removed. Everything else — ServiceM8
 * leave, closures, booked jobs — is read-only, and shown so the person editing
 * can see what they're working around rather than double-booking against it.
 */

import { useState } from 'react';
import type { AvailabilityForecast, BusinessHours } from '@cer-autofill/shared';
import { formatEventTime } from '../format.js';

type Day = AvailabilityForecast['inspectors'][number]['days'][number];
type Inspector = AvailabilityForecast['inspectors'][number];

export interface AvailabilityDayEditorProps {
  inspector: Inspector;
  day: Day;
  hours: BusinessHours;
  canEdit: boolean;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onAdd: (input: { start: string; end: string }) => void;
  onRemove: (blockId: string) => void;
}

/** Local wall-clock ISO the backend can parse, built from the day + minutes. */
function atMinutes(date: string, minutes: number): string {
  const value = new Date(`${date}T00:00:00`);
  value.setMinutes(value.getMinutes() + minutes);
  return value.toISOString();
}

function clock(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function minutesFromClock(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export function AvailabilityDayEditor({
  inspector,
  day,
  hours,
  canEdit,
  saving,
  error,
  onClose,
  onAdd,
  onRemove,
}: AvailabilityDayEditorProps) {
  const [start, setStart] = useState(() => clock(hours.startMinutes));
  const [end, setEnd] = useState(() => clock(hours.endMinutes));

  const startMinutes = minutesFromClock(start);
  const endMinutes = minutesFromClock(end);
  const invalid = endMinutes <= startMinutes;

  const ownBlocks = day.events.filter((event) => event.blockId);
  const external = day.events.filter((event) => !event.blockId);

  const dayLabel = new Date(`${day.date}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  function addPreset(fromMinutes: number, toMinutes: number) {
    onAdd({ start: atMinutes(day.date, fromMinutes), end: atMinutes(day.date, toMinutes) });
  }

  const midpoint = Math.round((hours.startMinutes + hours.endMinutes) / 2);

  return (
    <div className="drawer-backdrop" role="presentation" onClick={onClose}>
      <aside
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Availability for ${inspector.displayName}, ${dayLabel}`}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="drawer__header">
          <div>
            <h3>{inspector.displayName}</h3>
            <p className="muted">{dayLabel}</p>
          </div>
          <button type="button" className="ghost-btn" onClick={onClose}>
            Close
          </button>
        </header>

        {error && <p className="error">{error}</p>}

        <p className="drawer__summary">
          {day.status === 'non-working'
            ? 'Outside business days — no capacity to schedule.'
            : day.status === 'closed'
              ? 'Business closed.'
              : `${(day.availableMinutes / 60).toFixed(day.availableMinutes % 60 ? 1 : 0)}h free of ${(day.capacityMinutes / 60).toFixed(0)}h.`}
        </p>

        {canEdit && day.status !== 'non-working' && day.status !== 'closed' ? (
          <section className="drawer__section">
            <h4>Mark unavailable</h4>
            <div className="drawer__presets">
              <button
                type="button"
                className="ghost-btn"
                disabled={saving}
                onClick={() => addPreset(hours.startMinutes, hours.endMinutes)}
              >
                All day
              </button>
              <button
                type="button"
                className="ghost-btn"
                disabled={saving}
                onClick={() => addPreset(hours.startMinutes, midpoint)}
              >
                Morning
              </button>
              <button
                type="button"
                className="ghost-btn"
                disabled={saving}
                onClick={() => addPreset(midpoint, hours.endMinutes)}
              >
                Afternoon
              </button>
            </div>
            <div className="drawer__times">
              <label>
                <span>From</span>
                <input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
              </label>
              <label>
                <span>To</span>
                <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
              </label>
              <button
                type="button"
                disabled={saving || invalid}
                onClick={() =>
                  onAdd({
                    start: atMinutes(day.date, startMinutes),
                    end: atMinutes(day.date, endMinutes),
                  })
                }
              >
                {saving ? 'Saving…' : 'Add'}
              </button>
            </div>
            {invalid ? <p className="muted">The end time must be after the start.</p> : null}
            <p className="muted">
              No reason is recorded or shown — this says when you are unavailable, not why.
            </p>
          </section>
        ) : null}

        <section className="drawer__section">
          <h4>Declared in the dashboard</h4>
          {ownBlocks.length ? (
            <ul className="drawer__list">
              {ownBlocks.map((event) => (
                <li key={event.blockId}>
                  <span>
                    {formatEventTime(event.start)}–{formatEventTime(event.end)} {event.label}
                  </span>
                  {canEdit ? (
                    <button
                      type="button"
                      className="link-button"
                      disabled={saving}
                      onClick={() => onRemove(event.blockId!)}
                    >
                      Remove
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Nothing declared for this day.</p>
          )}
        </section>

        {external.length ? (
          <section className="drawer__section">
            <h4>From ServiceM8</h4>
            <ul className="drawer__list drawer__list--readonly">
              {external.map((event, index) => (
                <li key={index}>
                  <span>
                    {formatEventTime(event.start)}–{formatEventTime(event.end)} {event.label}
                  </span>
                  <span className="muted">
                    {event.kind === 'booking' ? 'Booked' : 'Unavailable'}
                  </span>
                </li>
              ))}
            </ul>
            <p className="muted">
              Recorded in ServiceM8 and read-only here. Change it there if it&apos;s wrong.
            </p>
          </section>
        ) : null}
      </aside>
    </div>
  );
}
